/* ── Runtime storage config + Azure Table helpers shared by the team pages ── */

/** Storage endpoints from runtime config, so they can change without a rebuild. */
export interface ScoreStoreConfig {
  blobUrl: string;
  sas: string;
}

export interface TablesConfig {
  endpoint: string;
  usersSas: string;
  messagesSas: string;
}

/** The blob folder message photos are uploaded to and served from. */
export interface PhotosConfig {
  baseUrl: string;
  sas: string;
}

export interface RuntimeSettings {
  scores: ScoreStoreConfig | null;
  tables: TablesConfig | null;
  photos: PhotosConfig | null;
}

export async function loadRuntimeSettings(): Promise<RuntimeSettings> {
  try {
    const response = await fetch('/appsettings.json', { cache: 'no-store' });
    if (!response.ok) return { scores: null, tables: null, photos: null };
    const settings = await response.json();
    const ts = settings?.TeamScores ?? {};
    const scores =
      typeof ts.BlobUrl === 'string' && ts.BlobUrl && typeof ts.Sas === 'string' && ts.Sas
        ? { blobUrl: ts.BlobUrl, sas: String(ts.Sas).replace(/^\?/, '') }
        : null;
    const tables =
      typeof ts.TableEndpoint === 'string' &&
      ts.TableEndpoint &&
      typeof ts.UsersSas === 'string' &&
      ts.UsersSas &&
      typeof ts.MessagesSas === 'string' &&
      ts.MessagesSas
        ? {
            endpoint: String(ts.TableEndpoint).replace(/\/$/, ''),
            usersSas: String(ts.UsersSas).replace(/^\?/, ''),
            messagesSas: String(ts.MessagesSas).replace(/^\?/, ''),
          }
        : null;
    const photos =
      typeof ts.PhotosBaseUrl === 'string' &&
      ts.PhotosBaseUrl &&
      typeof ts.PhotosSas === 'string' &&
      ts.PhotosSas
        ? {
            baseUrl: String(ts.PhotosBaseUrl).replace(/\/$/, ''),
            sas: String(ts.PhotosSas).replace(/^\?/, ''),
          }
        : null;
    return { scores, tables, photos };
  } catch {
    return { scores: null, tables: null, photos: null };
  }
}

/* ── Registration, via Azure Table Storage REST ────────────────────────── */

export interface TeamUser {
  email: string;
  nick: string;
}

/** The email a teammate registered with on the Team page, remembered per browser. */
export const USER_KEY = 'tlh-team-user-email';

export const ODATA_HEADERS = { Accept: 'application/json;odata=nometadata' };

/** OData string literals double their single quotes. */
export function odataQuote(value: string): string {
  return value.replace(/'/g, "''");
}

export async function lookupUser(tables: TablesConfig, email: string): Promise<TeamUser | null> {
  try {
    const url = `${tables.endpoint}/users(PartitionKey='user',RowKey='${encodeURIComponent(
      odataQuote(email),
    )}')?${tables.usersSas}`;
    const response = await fetch(url, { headers: ODATA_HEADERS });
    if (!response.ok) return null; // 404 → not registered
    const entity = await response.json();
    return { email, nick: typeof entity.NickName === 'string' ? entity.NickName : email };
  } catch {
    return null;
  }
}

/** Inverted-timestamp row key: table sort order becomes newest-first. */
export function messageRowKey(): string {
  const inverted = String(9_999_999_999_999 - Date.now()).padStart(13, '0');
  return `${inverted}-${Math.random().toString(36).slice(2, 8)}`;
}
